import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isNull, isUndefined, noop } from 'lodash/fp';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import styles from '../HomeWrapper.module.scss';
import { Icon } from '../../shared/icon/Icon';
import { CustomTooltip } from '../../shared/tooltip';
import { Scrollable } from '../../shared/scrollable/Scrollable';

const { primary, grey, white } = styles;

export enum MenuTileId {
    // User
    HOME = 'home',
    USER_DOCUMENTS_MODULE = 'user-documents-module',
    USER_OPINIONS_MODULE = 'user-opinions-module',
    USER_PLAYBOOKS = 'user-playbooks',
    USER_ATTESTATIONS = 'user-attestations',
    USER_CLAUSE_LIBRARY = 'user-clause-library',
    USER_DORA_MODULE = 'user-dora-module',
    USER_NEGOTIATION_GUIDES = 'user-negotiation-guides',
    USER_MY_DOCUMENTS = 'user-my-documents',
    USER_DOCUMENT_SEARCH = 'user-document-search',
    USER_DOCUMENT_REPORTING = 'user-document-reporting',
    USER_DOCUMENT_ANALYTICS = 'user-document-analytics',
    USER_MY_OPINIONS = 'user-my-opinions',
    USER_OPINION_REPORTING = 'user-opinion-reporting',
    USER_OPINION_ANALYTICS = 'user-opinion-analytics',
    USER_OPINION_NETTING_ENGINE = 'user-opinion-netting-engine',
    USER_DORA_DATA_MANAGEMENT = 'user-dora-data-management',
    USER_DORA_ANALYTICS = 'user-dora-analytics',
    USER_DORA_REPORTING = 'user-dora-reporting',
    // Admin
    USER_MANAGEMENT = 'user-management',
    COUNTERPARTY_MANAGEMENT = 'counterparty-management',
    DOCUMENT_MANAGEMENT = 'document-management',
    PLAYBOOK_MANAGEMENT = 'playbook-management',
    WORKFLOW = 'workflow',
    ANALYTICS = 'analytics',
    ATTESTATIONS = 'attestations',
    SYSTEM_MANAGEMENT = 'system-management',
    SYSTEM_DOCUMENT_MANAGEMENT = 'system-document-management',
    SYSTEM_AI_MANAGEMENT = 'system-ai-management',
    ENTITY_MANAGEMENT = 'entity-management',
    USERS = 'users',
    CLIENT_DATASETS = 'client-datasets',
    CLIENT_RISK_TOLERANCE = 'client-risk-tolerance',
    CLIENT_CALCULATOR_PROMPTS = 'client-calculator-prompts',
    SUB_COUNTERPARTY_MANAGEMENT = 'sub-counterparty-management',
    DOCUMENT_GROUP_MANAGEMENT = 'document-group-management',
    ACCESS_GROUPS = 'access-groups-management',
    PORTFOLIO_ANALYTICS = 'portfolio-analytics',
    ROLES = 'roles-management',
    // System
    SYSTEM_AI_DATA_EXTRACTION = 'system-ai-data-extraction',
    SYSTEM_AI_PLAYGROUND = 'system-ai-playground',
    SYSTEM_AI_ANALYTICS = 'system-ai-analytics',
    SYSTEM_AI_FEW_SHOT = 'system-ai-few-shot',
    SYSTEM_DATASETS = 'system-datasets',
    SYSTEM_RISK_TOLERANCE = 'system-risk-tolerance',
    SYSTEM_CALCULATOR_MANAGER = 'system-calculator-manager',
    SYSTEM_DOCUMENT_NAME = 'system-document-name',
    SYSTEM_OPINION_ANALYTICS = 'system-opinion-analytics',
    SYSTEM_DROPDOWN_LISTS = 'system-dropdown-lists',
    SYSTEM_CLIENT_MANAGEMENT = 'system-client-management',
    SYSTEM_NOTIFICATION = 'system-notification',
    SYSTEM_MAINTENANCE = 'system-maintenance'
}

export interface MenuTileProps {
    id: MenuTileId;
    label: string;
    icon: React.ElementType;
    shouldDisplay?: boolean;
    onClick: (id: MenuTileId) => void;
    selected?: MenuTileId | null;
    path?: string | undefined;
    parent?: MenuTileId;
    selectMenuTile?: () => void;
    disabled?: boolean;
    disabledTooltip?: string;
}

export const MenuTile: React.FC<MenuTileProps> = ({ id, label, icon, shouldDisplay = true, onClick, selected, selectMenuTile = noop, disabled = false, disabledTooltip }) => {

    const isSystem = useMemo(() => id.includes('system'), [id]);
    const color = useMemo(() => disabled ? grey : white, [disabled]);

    const handleClick = useCallback(() => {
        selectMenuTile();
        onClick(id);
    }, [onClick, id, selectMenuTile]);

    const minifiedTooltipOverlay = useMemo(() => disabledTooltip || label, [disabledTooltip, label]);
    const unselectedMinifiedTooltipOverlay = useMemo(() => disabledTooltip ? disabledTooltip : '', [disabledTooltip]);

    if (!shouldDisplay) {
        return null;
    }

    if (!isUndefined(selected) && !isNull(selected)) {
        return (
            <CustomTooltip overlayText={minifiedTooltipOverlay} placement='top'>
                <button className={classnames(styles.minifiedGroupTileWrapper, { [styles.systemMenuTile]: isSystem, [styles.disabledMenuTile]: disabled })} key={id} onClick={handleClick}>
                    <Icon icon={icon} fontSize={40} fill={primary} color={color} />
                </button>
            </CustomTooltip>
        );
    }

    return (
        <CustomTooltip overlayText={unselectedMinifiedTooltipOverlay} placement='top'>
            <button className={classnames(styles.groupTileWrapper, { [styles.systemMenuTile]: isSystem, [styles.disabledMenuTile]: disabled })} key={id} onClick={handleClick}>
                <Icon icon={icon} fontSize={50} fill={primary} color={color} />
                <div className={styles.groupTileLabel} style={{ color }}>{label}</div>
            </button>
        </CustomTooltip>
    );
};

export const MenuTileWithLink: React.FC<MenuTileProps> = ({ id, label, icon, shouldDisplay = true, onClick, selected, path, selectMenuTile = noop, disabled, disabledTooltip }) => {

    const isSystem = useMemo(() => id.includes('system'), [id]);
    const color = useMemo(() => disabled ? grey : white, [disabled]);

    const handleClick = useCallback(() => {
        selectMenuTile();
        onClick(id);
    }, [onClick, id, selectMenuTile]);

    const tooltipOverlay = useMemo(() => disabledTooltip || label, [disabledTooltip, label]);

    if (!shouldDisplay) {
        return null;
    }

    if (!isUndefined(selected) && !isNull(selected)) {
        return (
            <CustomTooltip overlayText={tooltipOverlay} placement='top'>
                <Link to={path!} className={styles.minifiedGroupTileWrapperWithLink}>
                    <button className={classnames(styles.minifiedWithLink, { [styles.systemMenuTile]: isSystem, [styles.disabledMenuTile]: disabled })} onClick={handleClick}>
                        <Icon icon={icon} fontSize={40} fill={primary} color={color} />
                    </button>
                </Link>
            </CustomTooltip>
        );
    }

    return (
        <Link to={path!} className={styles.groupTileWrapperWithLink}>
            <button className={classnames(styles.adminWithLink, { [styles.systemMenuTile]: isSystem, [styles.disabledMenuTile]: disabled })} key={id} onClick={handleClick}>
                <Icon icon={icon} fontSize={50} fill={primary} color={color} />
                <div className={styles.groupTileLabel} style={{ color }}>{label}</div>
            </button>
        </Link>
    );
};

export const TileButton: React.FC<MenuTileProps> = ({ ...props }) => {
    if (!isUndefined(props.path) && !props.disabled) {
        return (
            <MenuTileWithLink {...props} />
        );
    }
    return (
        <MenuTile {...props} />
    );
};

interface TilesMenuProps {
    tiles: MenuTileProps[];
    childTiles: MenuTileProps[];
    allTiles: MenuTileProps[];
    selected: MenuTileId | null;
    sortAlphabetically?: boolean;
    justifyContent?: 'flex-start' | 'flex-end';
}

export const TilesMenu: React.FC<TilesMenuProps> = ({ tiles, childTiles, allTiles, selected, justifyContent = 'flex-start', sortAlphabetically = false }) => {
    const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
    const [showSelectedContent, setShowSelectedContent] = useState<boolean>(false);

    const selectMenuTile = useCallback(() => {
        setShowSelectedContent(false);
        setShouldAnimate(true);
        setTimeout(() => setShouldAnimate(false), 10);
    }, []);

    const selectedTile = useMemo(() => !isNull(selected) && allTiles.find(({ id }) => id === selected) || null, [allTiles, selected]);

    const orderedTiles = useMemo(() => sortAlphabetically ? tiles.sort((a, b) => {
        if (a.id === MenuTileId.SYSTEM_MANAGEMENT) {
            return 1;
        }
        return a.label.localeCompare(b.label);
    }) : tiles, [tiles, sortAlphabetically]);
    const orderedChildTiles = useMemo(() => sortAlphabetically ? childTiles.sort((a, b) => a.label.localeCompare(b.label)) : childTiles, [childTiles, sortAlphabetically]);

    useEffect(() => {
        if (shouldAnimate) {
            setTimeout(() => setShowSelectedContent(true), 400);
        }
    }, [shouldAnimate]);

    if (isNull(selectedTile)) {
        return (
            <div className={styles.allTilesWrapper} style={{ justifyContent }}>
                {orderedTiles.map(tile => <TileButton key={tile.id} {...tile} selectMenuTile={selectMenuTile} />)}
            </div>
        );
    }

    const { label, icon } = selectedTile;
    return (
        <div className={styles.selectedWrapper}>
            <div className={styles.selectedTileWrapper}>
                {!shouldAnimate &&
                    <div className={styles.selectedTile}>
                        <div className={styles.selectedTileHeader}>
                            <div className={styles.selectedTileLabel}>{label}</div>
                            <Icon icon={icon} fontSize={50} fill={primary} />
                        </div>
                        {showSelectedContent &&
                            <div className={styles.scrollableChildren}>
                                <Scrollable>
                                    <div className={styles.allTilesWrapper} style={{ justifyContent }}>
                                        {orderedChildTiles.map(tile => <TileButton key={tile.id} {...tile} selectMenuTile={selectMenuTile} />)}
                                    </div>
                                </Scrollable>
                            </div>
                        }
                    </div>
                }
            </div>
            <div className={styles.remainingTilesWrapper}>
                {orderedTiles.filter(({ id }) => id !== selected).map(tile => <TileButton key={tile.id} {...tile} selected={selected} selectMenuTile={selectMenuTile} />)}
            </div>
        </div>
    );
};
